import { classes, belowViewPort } from "utils"
import { Input } from "./input.js"
import { Layout } from "./layout.js"
import { Button } from "./button.js"
import { NotificationContext } from "./notification.js"
import { useMemo, useRef, useCallback, useContext } from "react"
import { typography, colors } from "design"
import { useFormContext } from "react-hook-form"
import {
  asyncSuccess,
  asyncFailure,
  getRESTHeader,
  useService,
} from "utils"

const downloadFile = ({url, options }) => {
  const requestOptions = {
    method: "GET",
    headers: getRESTHeader(),
    ...options,
  }
  return fetch(`${BASE_URL}/${url}`, requestOptions).then(
    asyncSuccess,
    asyncFailure,
  )
}

export const FileInput = ({
  fieldName,
  downloadUrl,
  hideSelect,
  helpText,
  label,
  className,
  accept = (accept = ".jpeg, .jpg, .png, .pdf"),
  multiple,
  buttonLabel,
  downloadLabel,
  rules,
  watch,
  disabled,
}) => {
  const hiddenFileInputRef = useRef(null)
  const [, ,startDownload] = useService(downloadFile)
  const files = watch(fieldName)
  const { notifyError } = useContext(NotificationContext)
  const fileNames = useMemo(
    () =>
      _.isArray(files)
        ? _.map(files, (file) =>
            file.name ? file.name : _.last(_.split(file, "/")),
          )?.join(",")
        : (files?.name ?? downloadUrl?.split("/").pop()),
    [downloadUrl, files],
  )
  const { formState, setValue, clearErrors } = useFormContext()

  const buttonText = useMemo(
    () => buttonLabel || (multiple ? "SELECT FILES(S)" : "SELECT FILES"),
    [buttonLabel, multiple],
  )

  const downloadButtonText = useMemo(
    () => downloadLabel || (multiple ? "DOWNLOAD FILES(S)" : "DOWNLOAD FILE"),
    [downloadLabel, multiple],
  )

  const triggerUpload = useCallback(() => {
    hiddenFileInputRef.current.click()
  }, [])

  const onClickDownload = useCallback(() => {
    _.forEach(
      downloadUrl ? [downloadUrl] : _.split(files, ","),
      async (fileUrl) => {
        try {
          const blob = await startDownload({url:fileUrl})
          const url = window.URL.createObjectURL(blob) // Create a URL for the blob

          const link = document.createElement("a") // Create a temporary anchor element
          link.href = url
          link.download = _.split(fileUrl, "/").pop() // Set the desired filename

          document.body.appendChild(link) // Append the anchor to the body (for Firefox)
          link.click() // Programmatically trigger the click
          link.remove() // Remove the anchor from the document

          // Clean up the URL object
          window.URL.revokeObjectURL(url)
        } catch (error) {
          notifyError("Download failed:", error)
        }
      },
    )
  }, [downloadUrl, files, notifyError, startDownload])

  return (
    <>
      <Layout
        className="file-input-layout"
        css={{
          alignItems: formState.errors[fieldName] ? "center" : "flex-end",
          width: "100%",
          flexWrap: "wrap",
          gap: 10,
          justifyContent: "flex-end",
        }}
      >
        <Input
          className={classes(className, "file-input")}
          css={[{ flexGrow: 2 }, belowViewPort(768, { width: "100%" })]}
          label={label}
          rules={rules}
          fieldName={fieldName}
          value={fileNames}
          disabled
        />
        <input
          className="file-input-hidden"
          type="file"
          css={{ width: 0.122, visibility: "hidden", overflow: "hidden" }}
          ref={hiddenFileInputRef}
          multiple={multiple}
          accept={accept}
          onChange={(e) => {
            clearErrors(fieldName)
            setValue(
              fieldName,
              multiple ? Array.from(e.target.files) : e.target.files[0],
              {
                shouldDirty: true,
              },
            ) // We need to manually set the `dirty` value for file inputs
          }}
        />
        {!hideSelect && (
          <Button
            className="file-select-button"
            title={buttonText}
            css={{
              textWrap: "nowrap",
              marginTop: formState.errors[fieldName] ? 8 : 0,
              background: colors.cream
            }}
            type="button"
            onClick={triggerUpload}
            disabled={disabled}
            secondary
          />
        )}
        {downloadUrl && (
          <Button
            className="file-download-button"
            title={downloadButtonText}
            css={{
              textWrap: "nowrap",
              marginTop: formState.errors[fieldName] ? 8 : 0,
              background: colors.cream
            }}
            type="button"
            onClick={onClickDownload}
            disabled={disabled}
            secondary
          />
        )}
      </Layout>
      {helpText && (
        <div className="help-text" css={typography.helpText}>
          {helpText}
        </div>
      )}
    </>
  )
}
